if (!customElements.get('carousel-controls')) {
  customElements.define(
    'carousel-controls',
    class CarouselControls extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.prevBtn = this.querySelector('.slider-arrow.prev');
        this.nextBtn = this.querySelector('.slider-arrow.next');
        this.progressBar = this.querySelector('.progress-bar-fill');

        const targetSelector = this.getAttribute('data-target');
        if (!targetSelector) {
          console.warn('CarouselControls: No data-target attribute found.');
          return;
        }

        // Wait for the target to exist
        this.waitForTarget(targetSelector).then((target) => {
          this.container = target;
          this.init();
        });
      }

      waitForTarget(selector) {
        return new Promise((resolve) => {
          const target = document.querySelector(selector);
          if (target) return resolve(target);

          const observer = new MutationObserver((mutations, obs) => {
            const target = document.querySelector(selector);
            if (target) {
              obs.disconnect();
              resolve(target);
            }
          });

          observer.observe(document.body, {
            childList: true,
            subtree: true,
          });
        });
      }

      init() {
        this.updateProgress();
        this._updateProgress = this.updateProgress.bind(this);

        this.container.addEventListener('scroll', this._updateProgress);
        window.addEventListener('resize', this._updateProgress);

        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', () => this.scroll('left'));
        }

        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', () => this.scroll('right'));
        }
      }

      disconnectedCallback() {
        if (this.container && this._updateProgress) {
          this.container.removeEventListener('scroll', this._updateProgress);
        }
        if (this._updateProgress) {
          window.removeEventListener('resize', this._updateProgress);
        }
      }

      updateProgress() {
        if (!this.progressBar || !this.container) return;

        const items = this.#getSlideElements();

        if (items.length === 0) {
          this.progressBar.style.transform = 'scaleX(0)';
          return;
        }

        if (items.length === 1) {
          this.progressBar.style.transform = 'scaleX(1)';
          return;
        }

        const positions = items.map((el) => this.#getItemContentLeft(el));
        const index = this.#nearestItemIndex(positions, this.container.scrollLeft);
        const percentage = ((index + 1) / items.length) * 100;

        this.progressBar.style.transform = `scaleX(${percentage / 100})`;
      }

      #getSlideElements() {
        return Array.from(this.container.children).filter(
          (el) => el.nodeType === Node.ELEMENT_NODE && el.offsetParent !== null,
        );
      }

      #getItemContentLeft(item) {
        const c = this.container;
        return item.getBoundingClientRect().left - c.getBoundingClientRect().left + c.scrollLeft;
      }

      #nearestItemIndex(positions, scrollLeft) {
        if (positions.length === 0) {
          return 0;
        }

        let best = 0;
        let bestDist = Infinity;

        for (let i = 0; i < positions.length; i++) {
          const d = Math.abs(positions[i] - scrollLeft);

          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        }

        return best;
      }

      scroll(direction) {
        if (!this.container) {
          return;
        }

        const items = this.#getSlideElements();

        if (items.length === 0) {
          return;
        }

        const positions = items.map((el) => this.#getItemContentLeft(el));
        const scrollLeft = this.container.scrollLeft;
        let index = this.#nearestItemIndex(positions, scrollLeft);

        if (direction === 'left') {
          index = Math.max(0, index - 1);
        } else {
          index = Math.min(items.length - 1, index + 1);
        }

        const maxScroll = Math.max(0, this.container.scrollWidth - this.container.clientWidth);
        const targetLeft = Math.min(Math.max(0, positions[index]), maxScroll);

        this.container.scrollTo({
          left: targetLeft,
          behavior: 'smooth',
        });
      }
    },
  );
}
